import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WaveSurfer from 'wavesurfer.js';
import SpectrogramPlugin from 'wavesurfer.js/dist/plugins/spectrogram.esm.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import { fetchApi } from '../utils/api';
import { isAdmin } from '../utils/admin';

// Add this function before the StratagemView component
const convertWavToWebmUrl = (wavUrl) => {
  // Replace .wav with .webm in the URL
  return wavUrl.replace(/\.wav$/, '.webm');
};

// --- Player Hooks System ---
const playerHooks = {
  onPlayerInitialLoad: [],
  afterPlayerInitialLoad: [],
  onPlayerAutoPause: [],
  afterPlayerAutoPause: [],
  onPlayerReachEnd: [],
  afterPlayerReachEnd: [],
  onUserRecordAudio: [],
  afterUserRecordAudio: [],
  onLoadCombinedAudioPlayer: [],
  afterLoadCombinedAudioPlayer: [],
  onRegionsWillUpdate: [],
  afterRegionsUpdate: [],
};
function triggerHook(hookName, ...args) {
  if (playerHooks[hookName]) {
    console.log(`[HOOK] Triggering ${hookName}`, ...args);
    playerHooks[hookName].forEach(fn => fn(...args));
  }
}
// --- End Player Hooks System ---

// Add this helper at the top of the StratagemView component (or just before useEffect)
function waitForContainer(ws, cb, maxTries = 20) {
  let tries = 0;
  function check() {
    // Check for container in the renderer
    if (ws.renderer && ws.renderer.container) {
      cb();
    } else if (tries < maxTries) {
      tries++;
      setTimeout(check, 25);
    } else {
      console.warn('[HOOK] ws.renderer.container still undefined after waiting.');
    }
  }
  check();
}

// Add these utility functions before the StratagemView component
function forceRemoveAllRegionOverlays(container) {
  if (!container) return;
  
  // Remove all children of .wavesurfer-region-overlays
  const overlays = container.querySelector('.wavesurfer-region-overlays');
  if (overlays) {
    while (overlays.firstChild) overlays.removeChild(overlays.firstChild);
  }
  
  // Remove all .wavesurfer-region elements
  container.querySelectorAll('.wavesurfer-region').forEach(el => el.remove());
  
  // Remove all <region> elements
  container.querySelectorAll('region').forEach(el => el.remove());
  
  // Remove all phoneme labels
  container.querySelectorAll('.phoneme-label').forEach(el => el.remove());
  
  // Remove any remaining region-related elements
  container.querySelectorAll('[data-region-id]').forEach(el => el.remove());
  
  // Clear any region-related styles
  const styleElements = container.querySelectorAll('style');
  styleElements.forEach(style => {
    if (style.textContent.includes('wavesurfer-region')) {
      style.remove();
    }
  });
}

async function manageRegions(wsRegions, waveformRef, stratagem, audioFile, shiftAfter = null, shiftBy = 0, userRecordingAlignmentRef = null, playerStateRef = null, viewMode = 'syllable') {
  const container = waveformRef.current;
  if (!container) {
    console.warn('No container available for region management');
    return;
  }

  // Force remove all existing regions and overlays
  forceRemoveAllRegionOverlays(container);

  // Get the regions plugin from the WaveSurfer instance if not provided
  if (!wsRegions && wavesurferRef.current) {
    wsRegions = wavesurferRef.current.plugins?.regions;
  }

  if (!wsRegions) {
    console.error('No regions plugin available, cannot add regions');
    return;
  }

  // Clear existing regions
  wsRegions.clearRegions();
  
  // Clear the regions array in the plugin
  wsRegions.regions = [];
  
  // Wait a frame to ensure DOM updates
  await new Promise(resolve => requestAnimationFrame(resolve));

  // Only proceed with adding new regions if we're in a valid state
  if (!stratagem?.audio_clips?.[0]?.file) {
    console.warn('Invalid stratagem state for region management');
    return;
  }

  // Model clip chunks: saved editor edits if present, otherwise built from the alignment
  let regions = [];
  try {
    const { chunks } = await fetchApi(`/stratagems/${stratagem.id}/chunks?view=${viewMode}`);
    regions = shiftAfter !== null && shiftBy > 0
      ? chunks.map(c => (c.start >= shiftAfter ? { ...c, start: c.start + shiftBy, end: c.end + shiftBy } : c))
      : chunks;
  } catch (e) {
    console.error('Could not load chunks:', e);
  }

  // If we have user recording alignment data and we're in combined audio mode, add those regions too
  if (userRecordingAlignmentRef?.current && playerStateRef?.current?.isCombinedAudio) {
    const userRegions = userRecordingAlignmentRef.current.regions;
    let processedUserRegions = userRegions;

    if (viewMode === 'word') {
      // Group user phonemes into words
      const alignmentData = {
        words: []
      };

      // Group phonemes into words based on position markers
      let currentWord = null;
      userRegions.forEach(region => {
        const [arpabet, position] = region.arpabet.split('_');
        
        if (position === 'B' || !currentWord) {
          // Start a new word
          currentWord = {
            word: region.label,
            start: region.start,
            end: region.end,
            phones: [{
              phone: region.arpabet,
              start: region.start,
              end: region.end,
              duration: region.end - region.start
            }]
          };
          alignmentData.words.push(currentWord);
        } else {
          // Add to current word
          currentWord.end = region.end;
          currentWord.phones.push({
            phone: region.arpabet,
            start: region.start,
            end: region.end,
            duration: region.end - region.start
          });
        }
      });

      // Convert words to regions
      processedUserRegions = alignmentData.words.map(word => ({
        label: word.word,
        start: word.start + shiftAfter,
        end: word.end + shiftAfter
      }));
    } else if (viewMode === 'syllable') {
      // Group user phonemes into words first
      const alignmentData = {
        words: []
      };

      // Group phonemes into words based on position markers
      let currentWord = null;
      userRegions.forEach(region => {
        const [arpabet, position] = region.arpabet.split('_');
        
        if (position === 'B' || !currentWord) {
          // Start a new word
          currentWord = {
            word: region.label,
            start: region.start,
            end: region.end,
            phones: [{
              phone: region.arpabet,
              start: region.start,
              end: region.end,
              duration: region.end - region.start
            }]
          };
          alignmentData.words.push(currentWord);
        } else {
          // Add to current word
          currentWord.end = region.end;
          currentWord.phones.push({
            phone: region.arpabet,
            start: region.start,
            end: region.end,
            duration: region.end - region.start
          });
        }
      });

      // Process syllables using the syllableProcessor
      const syllableRegions = await fetchApi('/audio/process-syllables', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(alignmentData)
      });

      // Shift the syllable regions to the correct position
      processedUserRegions = syllableRegions.map(region => ({
        ...region,
        start: region.start + shiftAfter,
        end: region.end + shiftAfter
      }));
    } else {
      // For phoneme view, just shift the existing regions
      processedUserRegions = userRegions.map(region => ({
        ...region,
        start: region.start + shiftAfter,
        end: region.end + shiftAfter
      }));
    }

    regions = [...regions, ...processedUserRegions];
  }

  // Add regions after a short delay to ensure plugin is ready
  setTimeout(() => {
    regions.forEach((region, idx) => {
      try {
        const wsRegion = wsRegions.addRegion({
          start: region.start,
          end: region.end,
          color: idx % 2 === 0 ? 'rgba(111, 45, 168, 0.18)' : 'rgba(224, 64, 251, 0.10)',
          drag: false,
          resize: false,
        });
        const el = wsRegion && wsRegion.element;
        if (el && viewMode !== 'word') {  // Only add labels if not in word view
          // Remove any existing label
          const oldLabel = el.querySelector('.phoneme-label');
          if (oldLabel) oldLabel.remove();
          // Create new label
          const label = document.createElement('div');
          label.className = 'phoneme-label';
          label.textContent = region.label;
          label.style.bottom = '2px';
          label.style.position = 'absolute';
          label.style.left = '50%';
          label.style.transform = 'translateX(-50%)';
          label.style.fontWeight = 'bold';
          label.style.fontSize = '1.1em';
          label.style.color = '#222';
          label.style.textShadow = '0 1px 4px #fff, 0 0 2px #000';
          label.style.pointerEvents = 'none';
          label.style.zIndex = '10';
          label.style.userSelect = 'none';
          el.appendChild(label);
        } else if (!el) {
          console.warn('No element for region', region);
        }
      } catch (e) {
        console.error('Error adding region:', e, region);
      }
    });
  }, 100);
}

function StratagemView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stratagem, setStratagem] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingRecording, setIsProcessingRecording] = useState(false);
  const [userRecording, setUserRecording] = useState(null);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [viewMode, setViewMode] = useState('word');
  const userRecordingRef = useRef(null);
  const [showRecordingButton, setShowRecordingButton] = useState(false);
  const [currentSilenceRegion, setCurrentSilenceRegion] = useState(null);
  const silenceRegionRef = useRef(null);
  const pausePointRef = useRef(null);
  const [combinedAudio, setCombinedAudio] = useState(null);
  const [isCombinedMode, setIsCombinedMode] = useState(false);
  const isCombinedAudioRef = useRef(false);
  const hasPausedRef = useRef(false);
  const hasCombinedRef = useRef(false);
  const waveformRef = useRef(null);
  const spectrogramRef = useRef(null);
  const wavesurferRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const originalAudioBufferRef = useRef(null);
  const [audioContext, setAudioContext] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [audioSource, setAudioSource] = useState(null);
  const [audioAnalyser, setAudioAnalyser] = useState(null);
  const audioLevelRef = useRef(0);
  const maxLevelRef = useRef(0);
  const silenceRegionsRef = useRef([]);

  // Add player state object
  const playerStateRef = useRef({
    shouldAutoPause: false,
    pausePoint: null,
    silenceRegions: [],
    hasPaused: false,
    isCombinedAudio: false
  });

  // Add refs to persist pausePoint and userRecordingDuration
  const combinedPausePointRef = useRef(null);
  const combinedUserRecordingDurationRef = useRef(null);

  // Add a ref to track if we should switch to combined audio after the next finish
  const shouldSwitchToCombinedRef = useRef(false);

  // Add a ref to hold the pending combined audio URL
  const pendingCombinedAudioRef = useRef(null);
  const isAwaitingCombinedSwitchRef = useRef(false);

  // Add a ref for storing user recording alignment data
  const userRecordingAlignmentRef = useRef(null);

  // Define loadAudio function before WaveSurfer initialization
  const loadAudio = async (audioBuffer) => {
    // console.log('Loading audio...');
    if (isCombinedAudioRef.current) {
      // console.log('Loading combined audio, disabling auto-pause');
      playerStateRef.current = {
        shouldAutoPause: false,
        pausePoint: null,
        silenceRegions: [],
        hasPaused: false,
        isCombinedAudio: true
      };
    } else {
      // console.log('Loading original audio, enabling auto-pause');
      const silenceRegions = detectSilence(audioBuffer);
      const sortedRegions = silenceRegions.sort((a, b) => (b.end - b.start) - (a.end - a.start));
      const middleRegion = sortedRegions[0];
      const pauseTime = (middleRegion.start + middleRegion.end) / 2;
      
      playerStateRef.current = {
        shouldAutoPause: true,
        pausePoint: pauseTime,
        silenceRegions: silenceRegions,
        hasPaused: false,
        isCombinedAudio: false
      };
    }
  };

  // Fetch stratagem data
  useEffect(() => {
    // console.log('Fetching stratagem data...');
    const loadStratagem = async () => {
      try {
        const data = await fetchApi(`/stratagems/${id}`);
        // console.log('Stratagem data received:', data);
        setStratagem(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching stratagem:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    loadStratagem();
  }, [id]);

  // Fetch category data when we have a category_id
  useEffect(() => {
    const loadCategory = async () => {
      if (stratagem?.category_id) {
        try {
          const data = await fetchApi(`/categories/${stratagem.category_id}`);
          // console.log('Category data received:', data);
          setCategory(data);
        } catch (error) {
          console.error('Error fetching category:', error);
        }
      }
    };

    loadCategory();
  }, [stratagem?.category_id]);

  // Add debug logging for render
  // console.log('Current stratagem state:', stratagem);
  // console.log('Has category:', stratagem?.category);
  // console.log('Has category_id:', stratagem?.category_id);

  // Setup audio context and stream when component mounts
  useEffect(() => {
    const setupAudio = async () => {
      try {
        // Create audio context
        const ctx = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 48000
        });
        setAudioContext(ctx);

        // Get available devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioDevices = devices.filter(device => device.kind === 'audioinput');
        // console.log('Available audio devices:', audioDevices.map(d => ({
        //   deviceId: d.deviceId,
        //   label: d.label || 'Unnamed device',
        //   groupId: d.groupId
        // })));

        if (audioDevices.length === 0) {
          throw new Error('No audio input devices found');
        }

        // Try different audio configurations in order of preference
        let stream;
        const audioConfigs = [
          // Try default device with minimal constraints first
          {
            audio: true
          },
          // Then try with basic quality settings
          {
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            }
          },
          // Finally try with specific device and settings
          {
            audio: {
              deviceId: { ideal: audioDevices[0].deviceId },
              channelCount: { ideal: 2 },
              sampleRate: { ideal: 48000 },
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            }
          }
        ];

        // Try each configuration until one works
        for (const config of audioConfigs) {
          try {
            stream = await navigator.mediaDevices.getUserMedia(config);
            // console.log('Successfully got audio stream with config:', config);
            break;
          } catch (error) {
            // console.log('Failed to get stream with config:', config, error);
            continue;
          }
        }

        if (!stream) {
          throw new Error('Could not get audio stream with any configuration');
        }

        setAudioStream(stream);

        // Set up audio monitoring
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.3;
        source.connect(analyser);
        setAudioSource(source);
        setAudioAnalyser(analyser);

        // Start monitoring audio levels using both frequency and time domain
        const freqArray = new Uint8Array(analyser.frequencyBinCount);
        const timeArray = new Float32Array(analyser.fftSize);
        let lastLevel = 0;
        let silenceCount = 0;

        const checkAudioLevel = () => {
          if (analyser) {
            // Get both frequency and time domain data
            analyser.getByteFrequencyData(freqArray);
            analyser.getFloatTimeDomainData(timeArray);

            // Calculate RMS value from time domain data
            let sum = 0;
            for (let i = 0; i < timeArray.length; i++) {
              sum += timeArray[i] * timeArray[i];
            }
            const rms = Math.sqrt(sum / timeArray.length);
            
            // Calculate average frequency
            const avgFreq = freqArray.reduce((a, b) => a + b, 0) / freqArray.length;
            
            // Update the audio level ref with the maximum of both measurements
            const level = Math.max(rms * 1000, avgFreq); // Increased multiplier for RMS
            audioLevelRef.current = level;
            maxLevelRef.current = Math.max(maxLevelRef.current, level);

            // Detect significant changes in audio level
            if (Math.abs(level - lastLevel) > 2) { // Reduced threshold
              // console.log('Audio level changed:', {
              //   rms: rms.toFixed(4),
              //   avgFreq: avgFreq.toFixed(2),
              //   level: level.toFixed(2),
              //   maxLevel: maxLevelRef.current.toFixed(2)
              // });
            }

            // Log if we detect silence for too long
            if (level < 0.1) { // Reduced threshold
              silenceCount++;
              if (silenceCount > 100) {
                // console.log('Warning: No audio input detected for extended period. Current level:', level.toFixed(4));
                silenceCount = 0;
              }
            } else {
              silenceCount = 0;
            }

            lastLevel = level;
          }
          requestAnimationFrame(checkAudioLevel);
        };
        checkAudioLevel();

        // Create media recorder with best available codec
        const mimeType = 'audio/webm;codecs=opus';  // Force WebM/Opus for best quality
        const recorder = new MediaRecorder(stream, {
          mimeType: mimeType,
          audioBitsPerSecond: 192000  // 192kbps for high quality
        });

        // Add event listeners to the recorder for debugging
        recorder.onstart = () => {
          // console.log('MediaRecorder started with settings:', {
          //   mimeType: recorder.mimeType,
          //   audioBitsPerSecond: recorder.audioBitsPerSecond,
          //   state: recorder.state,
          //   maxLevel: maxLevelRef.current.toFixed(2)
          // });
        };

        recorder.onerror = (event) => {
          console.error('MediaRecorder error:', event);
        };

        setMediaRecorder(recorder);
        // console.log('Audio setup complete, using mime type:', mimeType);
        // console.log('Using audio device:', audioDevices[0].label || 'Default device');
      } catch (error) {
        console.error('Error setting up audio:', error);
        setError('Failed to setup audio: ' + error.message);
      }
    };

    setupAudio();

    // Cleanup function
    return () => {
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
      }
      if (audioContext) {
        audioContext.close();
      }
    };
  }, []);

  const detectSilence = (audioData) => {
    // Increased threshold and decreased minimum duration
    const threshold = 0.05; // Increased from 0.01
    const minSilenceDuration = 0.2; // Decreased from 0.5
    
    // console.log('Starting silence detection...');
    // console.log('Audio data length:', audioData.length);
    // console.log('Sample rate:', audioData.sampleRate);
    // console.log('Total duration:', audioData.length / audioData.sampleRate, 'seconds');
    
    let silenceStart = null;
    const silenceRegions = [];
    let maxAmplitude = 0;

    // Get the first channel's data
    const channelData = audioData.getChannelData(0);
    // console.log('Channel data length:', channelData.length);
    // console.log('First 10 samples:', Array.from(channelData.slice(0, 10)));

    // First pass: find max amplitude for normalization
    for (let i = 0; i < channelData.length; i++) {
      const amplitude = Math.abs(channelData[i]);
      if (amplitude > maxAmplitude) {
        maxAmplitude = amplitude;
      }
    }

    // console.log('Max amplitude:', maxAmplitude);
    const normalizedThreshold = threshold * maxAmplitude;
    // console.log('Normalized threshold:', normalizedThreshold);

    // Second pass: detect silence using normalized threshold
    for (let i = 0; i < channelData.length; i++) {
      const amplitude = Math.abs(channelData[i]);
      if (amplitude < normalizedThreshold) {
        if (silenceStart === null) {
          silenceStart = i / audioData.sampleRate;
        }
      } else {
        if (silenceStart !== null) {
          const silenceEnd = i / audioData.sampleRate;
          const duration = silenceEnd - silenceStart;
          if (duration >= minSilenceDuration) {
            // console.log(`Found silence region: ${silenceStart.toFixed(2)}s to ${silenceEnd.toFixed(2)}s (duration: ${duration.toFixed(2)}s)`);
            silenceRegions.push({ start: silenceStart, end: silenceEnd });
          }
          silenceStart = null;
        }
      }
    }

    // Check if we ended in silence
    if (silenceStart !== null) {
      const silenceEnd = channelData.length / audioData.sampleRate;
      const duration = silenceEnd - silenceStart;
      if (duration >= minSilenceDuration) {
        // console.log(`Found silence region at end: ${silenceStart.toFixed(2)}s to ${silenceEnd.toFixed(2)}s (duration: ${duration.toFixed(2)}s)`);
        silenceRegions.push({ start: silenceStart, end: silenceEnd });
      }
    }

    // console.log('Silence detection complete. Found', silenceRegions.length, 'silence regions:');
    silenceRegions.forEach((region, index) => {
      // console.log(`Region ${index + 1}: ${region.start.toFixed(2)}s to ${region.end.toFixed(2)}s`);
    });

    // Sort regions by duration and get the longest one (middle region)
    const sortedRegions = silenceRegions.sort((a, b) => (b.end - b.start) - (a.end - a.start));
    const middleRegion = sortedRegions[0];
    // console.log('Selected middle silence region:', middleRegion);

    return silenceRegions;
  };

  const startRecording = async () => {
    try {
      if (!mediaRecorder || !audioContext) {
        throw new Error('Audio not initialized');
      }

      console.log('Starting recording...');
      const audioChunks = [];
      let totalChunkSize = 0;
      let hasAudioData = false;
      let maxChunkSize = 0;

      mediaRecorder.ondataavailable = (event) => {
        console.log('Recording data available, chunk size:', event.data.size);
        if (event.data.size > 0) {
          audioChunks.push(event.data);
          totalChunkSize += event.data.size;
          maxChunkSize = Math.max(maxChunkSize, event.data.size);
          // Consider it has audio data if we get a chunk larger than 1KB
          if (event.data.size > 1024) {
            hasAudioData = true;
          }
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('Recording stopped, creating blob...');
        console.log('Total chunks:', audioChunks.length);
        console.log('Total chunk size:', totalChunkSize);
        console.log('Max chunk size:', maxChunkSize);
        console.log('Audio level during recording:', audioLevelRef.current);
        console.log('Max audio level detected:', maxLevelRef.current);
        
        try {
          if (!hasAudioData) {
            throw new Error('No audio input detected during recording. Please check your microphone settings.');
          }

          const audioBlob = new Blob(audioChunks, { type: mediaRecorder.mimeType });
          console.log('Recording blob size:', audioBlob.size);
          
          const arrayBuffer = await audioBlob.arrayBuffer();
          console.log('Array buffer size:', arrayBuffer.byteLength);
          
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          // Calculate max/min values more efficiently
          const channel0 = audioBuffer.getChannelData(0);
          let maxValue = -Infinity;
          let minValue = Infinity;
          
          // Process in chunks to avoid stack overflow
          const chunkSize = 10000;
          for (let i = 0; i < channel0.length; i += chunkSize) {
            const end = Math.min(i + chunkSize, channel0.length);
            for (let j = i; j < end; j++) {
              maxValue = Math.max(maxValue, Math.abs(channel0[j]));
              minValue = Math.min(minValue, channel0[j]);
            }
          }
          
          // If we have a second channel, process it too
          if (audioBuffer.numberOfChannels > 1) {
            const channel1 = audioBuffer.getChannelData(1);
            for (let i = 0; i < channel1.length; i += chunkSize) {
              const end = Math.min(i + chunkSize, channel1.length);
              for (let j = i; j < end; j++) {
                maxValue = Math.max(maxValue, Math.abs(channel1[j]));
                minValue = Math.min(minValue, channel1[j]);
              }
            }
          }
          
          console.log('Decoded audio buffer:', {
            sampleRate: audioBuffer.sampleRate,
            numberOfChannels: audioBuffer.numberOfChannels,
            length: audioBuffer.length,
            duration: audioBuffer.duration,
            maxValue: maxValue,
            minValue: minValue
          });

          // Only normalize if we have actual audio data
          if (maxValue > 0) {
            // Create a stereo buffer for output
            const normalizedBuffer = audioContext.createBuffer(
              2, // Always output stereo
              audioBuffer.length,
              audioBuffer.sampleRate
            );

            const targetMaxAmplitude = 0.696; // Match original audio's max amplitude
            const gainFactor = targetMaxAmplitude / maxValue;

            // Process normalization in chunks
            for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
              const inputData = audioBuffer.getChannelData(channel);
              const outputData = normalizedBuffer.getChannelData(channel);
              
              for (let i = 0; i < inputData.length; i += chunkSize) {
                const end = Math.min(i + chunkSize, inputData.length);
                for (let j = i; j < end; j++) {
                  outputData[j] = inputData[j] * gainFactor;
                }
              }
            }

            // If input was mono, copy to both channels
            if (audioBuffer.numberOfChannels === 1) {
              const monoData = normalizedBuffer.getChannelData(0);
              const stereoData = normalizedBuffer.getChannelData(1);
              stereoData.set(monoData);
            }
            
            // Create a new WAV blob with high quality settings
            const wavBlob = await audioBufferToWav(normalizedBuffer);
            console.log('WAV blob size:', wavBlob.size);
            
            const audioUrl = URL.createObjectURL(wavBlob);
            console.log('Recording saved:', audioUrl);
            setUserRecording(audioUrl);
            userRecordingRef.current = audioUrl;

            // Combine the recording with the original audio, then replay the full sequence
            console.log('Combining recording with original audio...');
            await combineAudioWithRecording(wavBlob);
          } else {
            throw new Error('No audio data detected in recording');
          }
        } catch (error) {
          console.error('Error processing recording:', error);
          setError('Failed to process recording: ' + error.message);
          setIsProcessingRecording(false);
          setShowRecordingButton(true);
          
          // Clean up any resources
          if (mediaRecorderRef.current) {
            mediaRecorderRef.current = null;
          }
        }
      };

      // Request data every 1000ms for more reliable recording
      mediaRecorder.start(1000);
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      console.log('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Failed to start recording: ' + error.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      console.log('Stopping recording...');
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsProcessingRecording(true);
      
      // Keep playback paused while the recording is processed and combined
      if (wavesurferRef.current) {
        wavesurferRef.current.pause();
      }
    }
  };

  const resampleAudioBuffer = async (sourceBuffer, targetSampleRate) => {
    console.log('Resampling audio:', {
      sourceSampleRate: sourceBuffer.sampleRate,
      targetSampleRate: targetSampleRate,
      sourceChannels: sourceBuffer.numberOfChannels,
      sourceLength: sourceBuffer.length,
      sourceDuration: sourceBuffer.duration
    });

    // If the sample rates are the same, return the original buffer
    if (sourceBuffer.sampleRate === targetSampleRate) {
      console.log('Sample rates match, returning original buffer');
      return sourceBuffer;
    }

    const audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: targetSampleRate
    });
    
    // Calculate the new length based on the sample rate ratio
    const ratio = targetSampleRate / sourceBuffer.sampleRate;
    const newLength = Math.round(sourceBuffer.length * ratio);
    
    console.log('Creating offline context:', {
      channels: sourceBuffer.numberOfChannels,
      length: newLength,
      sampleRate: targetSampleRate
    });

    // Create an offline context for resampling
    const offlineContext = new OfflineAudioContext(
      sourceBuffer.numberOfChannels,
      newLength,
      targetSampleRate
    );

    // Create a buffer source
    const source = offlineContext.createBufferSource();
    source.buffer = sourceBuffer;
    
    // Add a gain node to prevent clipping
    const gainNode = offlineContext.createGain();
    gainNode.gain.value = 0.95;  // Slightly reduce gain to prevent clipping
    
    // Add a compressor to maintain consistent levels
    const compressor = offlineContext.createDynamicsCompressor();
    compressor.threshold.value = -24;
    compressor.knee.value = 30;
    compressor.ratio.value = 12;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;
    
    // Connect the nodes directly without the low-pass filter
    source.connect(compressor);
    compressor.connect(gainNode);
    gainNode.connect(offlineContext.destination);
    source.start();

    // Render the resampled audio
    const resampledBuffer = await offlineContext.startRendering();
    
    console.log('Resampling complete:', {
      originalSampleRate: sourceBuffer.sampleRate,
      targetSampleRate: targetSampleRate,
      originalLength: sourceBuffer.length,
      resampledLength: resampledBuffer.length,
      originalDuration: sourceBuffer.duration,
      resampledDuration: resampledBuffer.duration
    });

    return resampledBuffer;
  };

  const switchToCombinedAudio = async (combinedAudioUrl, { autoPlay = false } = {}) => {
    const ws = wavesurferRef.current;
    if (!ws) {
      console.error('Cannot switch to combined audio: WaveSurfer not initialized');
      return;
    }

    try {
      console.log('Loading combined audio into player...');
      setPlayerLoading(true);
      isAwaitingCombinedSwitchRef.current = false;

      ws.pause();

      const regionsPlugin = ws.plugins?.regions;
      if (regionsPlugin) {
        console.log('Clearing regions before loading combined audio...');
        regionsPlugin.clearRegions();
        const container = waveformRef.current;
        if (container) {
          const overlays = container.querySelector('.wavesurfer-region-overlays');
          if (overlays) overlays.innerHTML = '';
          container.querySelectorAll('.phoneme-label').forEach(label => label.remove());
        }
      }

      await ws.load(combinedAudioUrl);
      ws.seekTo(0);

      setCombinedAudio(combinedAudioUrl);
      setIsCombinedMode(true);
      setIsProcessingRecording(false);
      setShowRecordingButton(false);
      console.log('Combined audio loaded successfully, duration:', ws.getDuration());

      if (autoPlay) {
        console.log('Playing combined audio (model → student → model)...');
        ws.play();
      }
    } catch (error) {
      console.error('Error loading combined audio:', error);
      setError('Failed to load combined audio: ' + error.message);
      setPlayerLoading(false);
      setIsProcessingRecording(false);
      setShowRecordingButton(true);
      isCombinedAudioRef.current = false;
      hasCombinedRef.current = false;
      pendingCombinedAudioRef.current = null;
      setCombinedAudio(null);
      setIsCombinedMode(false);
    }
  };

  const combineAudioWithRecording = async (userRecordingBlob) => {
    try {
      console.log('Starting audio combination...');
      
      // Create an AudioContext with 48kHz sample rate
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 48000
      });
      
      // Decode the user's recording
      console.log('Decoding user recording...');
      const userRecordingArrayBuffer = await userRecordingBlob.arrayBuffer();
      const userRecordingBuffer = await audioContext.decodeAudioData(userRecordingArrayBuffer);
      console.log('User recording decoded:', {
        sampleRate: userRecordingBuffer.sampleRate,
        numberOfChannels: userRecordingBuffer.numberOfChannels,
        length: userRecordingBuffer.length,
        duration: userRecordingBuffer.duration
      });

      // Get the pause point from the player state
      const state = playerStateRef.current;
      const pausePoint = state.pausePoint;
      const userRecordingDuration = userRecordingBuffer.duration;
      console.log('Using pause point from player state:', pausePoint);
      
      if (!pausePoint) {
        throw new Error('No pause point found in player state');
      }

      // Get phoneme alignment for user recording
      console.log('Getting phoneme alignment for user recording...');
      const alignmentResponse = await getPhonemeAlignment(userRecordingBlob, stratagem.name);

      // Store the alignment data
      userRecordingAlignmentRef.current = alignmentResponse;

      // Convert pause point from seconds to samples
      const pausePointSamples = Math.floor(pausePoint * 48000);
      
      // Add crossfade to prevent clicks
      const crossfadeDuration = 0.01;
      const crossfadeSamples = Math.floor(crossfadeDuration * 48000);  // 48kHz
      
      // Calculate the total length needed
      const remainingOriginalAudio = originalAudioBufferRef.current.length - pausePointSamples;
      const totalLength = pausePointSamples + userRecordingBuffer.length + remainingOriginalAudio;
      
      console.log('Creating final buffer:', {
        channels: 2,
        length: totalLength,
        sampleRate: 48000,
        pausePoint: pausePointSamples,
        remainingOriginalAudio: remainingOriginalAudio,
        userRecordingLength: userRecordingBuffer.length
      });

      // Create the final buffer
      const finalBuffer = audioContext.createBuffer(
        2,  // Stereo
        totalLength,
        48000  // 48kHz
      );

      // Copy the first part of the original audio with crossfade
      for (let channel = 0; channel < 2; channel++) {
        const originalData = originalAudioBufferRef.current.getChannelData(channel);
        const finalData = finalBuffer.getChannelData(channel);
        
        // Copy up to crossfade point
        finalData.set(originalData.slice(0, pausePointSamples - crossfadeSamples));
        
        // Apply crossfade
        for (let i = 0; i < crossfadeSamples; i++) {
          const fadeOut = 1 - (i / crossfadeSamples);
          const fadeIn = i / crossfadeSamples;
          const originalSample = originalData[pausePointSamples - crossfadeSamples + i];
          const recordingSample = userRecordingBuffer.getChannelData(channel)[i];
          finalData[pausePointSamples - crossfadeSamples + i] = 
            (originalSample * fadeOut) + (recordingSample * fadeIn);
        }
      }

      // Copy the user recording
      for (let channel = 0; channel < 2; channel++) {
        const recordingData = userRecordingBuffer.getChannelData(channel);
        const finalData = finalBuffer.getChannelData(channel);
        finalData.set(recordingData, pausePointSamples);
      }

      // Copy the remaining original audio with crossfade
      const remainingStartPoint = pausePointSamples + userRecordingBuffer.length;
      for (let channel = 0; channel < 2; channel++) {
        const originalData = originalAudioBufferRef.current.getChannelData(channel);
        const finalData = finalBuffer.getChannelData(channel);
        
        // Apply crossfade at the end of the recording
        for (let i = 0; i < crossfadeSamples; i++) {
          const fadeOut = 1 - (i / crossfadeSamples);
          const fadeIn = i / crossfadeSamples;
          const recordingSample = userRecordingBuffer.getChannelData(channel)[userRecordingBuffer.length - crossfadeSamples + i];
          const originalSample = originalData[pausePointSamples + i];
          finalData[remainingStartPoint - crossfadeSamples + i] = 
            (recordingSample * fadeOut) + (originalSample * fadeIn);
        }
        
        // Copy the rest of the original audio
        finalData.set(originalData.slice(pausePointSamples + crossfadeSamples), remainingStartPoint);
      }

      // Convert the final buffer to WAV and create a URL
      const wavBlob = await audioBufferToWav(finalBuffer);
      const combinedAudioUrl = URL.createObjectURL(wavBlob);
      console.log('Created combined audio URL:', combinedAudioUrl);
      
      // Store the combined audio URL and update state
      pendingCombinedAudioRef.current = combinedAudioUrl;
      isCombinedAudioRef.current = true;
      hasCombinedRef.current = true;
      isAwaitingCombinedSwitchRef.current = true;
      
      // Update player state with new configuration
      playerStateRef.current = {
        shouldAutoPause: false,
        pausePoint: null,
        silenceRegions: [],
        hasPaused: false,
        isCombinedAudio: true
      };

      // Store refs to persist pausePoint and userRecordingDuration
      combinedPausePointRef.current = pausePoint;
      combinedUserRecordingDurationRef.current = userRecordingDuration;

      await switchToCombinedAudio(combinedAudioUrl, { autoPlay: true });
      
    } catch (error) {
      console.error('Error combining audio:', error);
      setError('Failed to combine audio: ' + error.message);
      setIsProcessingRecording(false);
      setShowRecordingButton(true);
      
      // Reset state on error
      isCombinedAudioRef.current = false;
      hasCombinedRef.current = false;
      pendingCombinedAudioRef.current = null;
      isAwaitingCombinedSwitchRef.current = false;
      userRecordingAlignmentRef.current = null;
      setCombinedAudio(null);
      setIsCombinedMode(false);
    }
  };

  const getPhonemeAlignment = async (audioBlob, transcript) => {
    console.log('Getting phoneme alignment for user recording...');
    
    // Create form data
    const formData = new FormData();
    formData.append('audio', audioBlob);
    formData.append('transcript', transcript);

    console.log('Waiting for Gentle alignment response...');
    
    try {
        const alignmentResponse = await fetchApi('/audio/align-recording', {
            method: 'POST',
            headers: {
                // Don't set Content-Type here, let the browser set it with the boundary
                'Accept': 'application/json',
            },
            body: formData,
            credentials: 'include'
        });

        if (!alignmentResponse || !alignmentResponse.words) {
            throw new Error('Invalid alignment response');
        }

        console.log('Alignment API response:', alignmentResponse);
        return alignmentResponse;
    } catch (error) {
        console.error('Error getting phoneme alignment:', error);
        throw error;
    }
  };

  // Helper function to convert AudioBuffer to WAV
  const audioBufferToWav = async (buffer) => {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;

    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = buffer.length * blockAlign;
    const headerSize = 44;
    const totalSize = headerSize + dataSize;

    const arrayBuffer = new ArrayBuffer(totalSize);
    const view = new DataView(arrayBuffer);

    // RIFF identifier
    writeString(view, 0, 'RIFF');
    // RIFF chunk length
    view.setUint32(4, totalSize - 8, true);
    // RIFF type
    writeString(view, 8, 'WAVE');
    // format chunk identifier
    writeString(view, 12, 'fmt ');
    // format chunk length
    view.setUint32(16, 16, true);
    // sample format (raw)
    view.setUint16(20, format, true);
    // channel count
    view.setUint16(22, numChannels, true);
    // sample rate
    view.setUint32(24, sampleRate, true);
    // byte rate (sample rate * block align)
    view.setUint32(28, byteRate, true);
    // block align (channel count * bytes per sample)
    view.setUint16(32, blockAlign, true);
    // bits per sample
    view.setUint16(34, bitDepth, true);
    // data chunk identifier
    writeString(view, 36, 'data');
    // data chunk length
    view.setUint32(40, dataSize, true);

    // Write the PCM samples
    const offset = 44;
    const channelData = [];
    for (let i = 0; i < numChannels; i++) {
      channelData.push(buffer.getChannelData(i));
    }

    let pos = 0;
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < numChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channelData[channel][i]));
        const value = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset + pos, value, true);
        pos += 2;
      }
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const resetRecording = async () => {
    console.log('Resetting recording state...');
    
    // Set player loading state
    setPlayerLoading(true);
    
    // Immediately stop playback and reset position
    if (wavesurferRef.current) {
      wavesurferRef.current.stop();
      wavesurferRef.current.seekTo(0);
    }
    
    // Reset all state variables to initial configuration
    isCombinedAudioRef.current = false;
    hasCombinedRef.current = false;
    hasPausedRef.current = false;
    isAwaitingCombinedSwitchRef.current = false;
    pendingCombinedAudioRef.current = null;
    userRecordingRef.current = null;
    silenceRegionRef.current = null;
    pausePointRef.current = null;
    silenceRegionsRef.current = [];
    
    // Reset player state to initial configuration
    playerStateRef.current = {
      shouldAutoPause: true,
      pausePoint: null,
      silenceRegions: [],
      hasPaused: false,
      isCombinedAudio: false
    };
    
    // Reset React state
    setUserRecording(null);
    setCurrentSilenceRegion(null);
    setCombinedAudio(null);
    setIsCombinedMode(false);
    setIsProcessingRecording(false);
    setShowRecordingButton(false);
    setIsPlaying(false);
    
    // Reload the original audio
    if (wavesurferRef.current) {
      try {
        // Create a new AudioContext for decoding
        const audioContext = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 48000
        });

        // Try to load the audio file directly as WAV first
        const wavUrl = `/audio/stratagems/${stratagem.id}/${stratagem.audio_clips[0].file}`;
        const response = await fetch(wavUrl);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        console.log('Audio decoded successfully');

        // Store the buffer for later use
        originalAudioBufferRef.current = audioBuffer;
        
        // Set up pause-for-recording for original audio
        console.log('Setting up pause-for-recording for original audio');
        const silenceRegions = detectSilence(audioBuffer);
        silenceRegionsRef.current = silenceRegions;
        const sortedRegions = silenceRegions.sort((a, b) => (b.end - b.start) - (a.end - a.start));
        const middleRegion = sortedRegions[0];
        const pauseTime = (middleRegion.start + middleRegion.end) / 2;
        
        // Update player state with new silence regions and pause point
        playerStateRef.current = {
          shouldAutoPause: true,
          pausePoint: pauseTime,
          silenceRegions: silenceRegions,
          hasPaused: false,
          isCombinedAudio: false
        };

        // Load the WAV file directly
        wavesurferRef.current.load(wavUrl);
      } catch (error) {
        console.error('Error loading audio:', error);
        setError('Failed to load audio: ' + error.message);
        setPlayerLoading(false);  // Make sure to reset loading state on error
      }
    }
  };

  // Reset all states when loading a new stratagem
  useEffect(() => {
    hasPausedRef.current = false;
    userRecordingRef.current = null;
    silenceRegionRef.current = null;
    pausePointRef.current = null;
    isCombinedAudioRef.current = false;
    hasCombinedRef.current = false;
    setUserRecording(null);
    setCurrentSilenceRegion(null);
    setCombinedAudio(null);
    setIsCombinedMode(false);
    setIsProcessingRecording(false);
    shouldSwitchToCombinedRef.current = false;
    pendingCombinedAudioRef.current = null;
    isAwaitingCombinedSwitchRef.current = false;
  }, [stratagem?.id]);

  // Initialize WaveSurfer
  useEffect(() => {
    // Only create WaveSurfer if the container ref and audio file are available
    if (!waveformRef.current || !stratagem?.audio_clips?.[0]?.file) {
      // console.log('WaveSurfer creation skipped: waveformRef.current or audio file not ready.', {
      //   waveformRef: waveformRef.current,
      //   stratagem,
      // });
      return;
    }
    // Log the container ref before creating WaveSurfer
    // console.log('waveformRef.current at WaveSurfer creation:', waveformRef.current);

    // Destroy previous WaveSurfer instance if it exists
    if (wavesurferRef.current) {
      // console.log('Destroying previous WaveSurfer instance');
      if (waveformRef.current) {
        forceRemoveAllRegionOverlays(waveformRef.current);
        waveformRef.current.innerHTML = '';
      }
      if (spectrogramRef.current) spectrogramRef.current.innerHTML = '';
      try {
        // Properly destroy regions plugin first
        if (wavesurferRef.current.plugins?.regions) {
          const regionsPlugin = wavesurferRef.current.plugins.regions;
          regionsPlugin.clearRegions();
          // Remove all region elements from the DOM
          const container = waveformRef.current;
          if (container) {
            const overlays = container.querySelector('.wavesurfer-region-overlays');
            if (overlays) overlays.innerHTML = '';
            container.querySelectorAll('.wavesurfer-region').forEach(el => el.remove());
            container.querySelectorAll('region').forEach(el => el.remove());
            container.querySelectorAll('.phoneme-label').forEach(el => el.remove());
          }
          // Unregister the plugin
          wavesurferRef.current.unregisterPlugin(regionsPlugin);
        }
        wavesurferRef.current.destroy();
      } catch (e) {
        console.warn('Error destroying WaveSurfer:', e);
      }
      wavesurferRef.current = null;
    }

    // Now safe to create WaveSurfer
    try {
      // Create a new regions plugin instance
      const regionsPlugin = RegionsPlugin.create({
        dragSelection: false,
        color: 'rgba(111, 45, 168, 0.18)',
        slop: 5
      });

      const wavesurfer = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: '#4F4A85',
        progressColor: '#e040fb',
        height: 100,
        responsive: true,
        cursorColor: '#383351',
        backend: 'WebAudio',
        plugins: [
          SpectrogramPlugin.create({
            container: spectrogramRef.current,
            labels: true,
            height: 100,
            splitChannels: false
          }),
          regionsPlugin
        ]
      });

      // Store the regions plugin reference
      wavesurfer.plugins.regions = regionsPlugin;

      // Wait for the container to be ready before proceeding
      waitForContainer(wavesurfer, () => {
        // console.log('WaveSurfer container ready, checking regions plugin...');
        if (wavesurfer.plugins && wavesurfer.plugins.regions) {
          // console.log('Regions plugin found:', wavesurfer.plugins.regions);
        } else {
          console.warn('Regions plugin not found in plugins:', wavesurfer.plugins);
        }
      });

      wavesurferRef.current = wavesurfer;

      // Add event listeners
      wavesurfer.on('play', () => {
        // console.log('WaveSurfer play event');
        setIsPlaying(true);
        
        const state = playerStateRef.current;
        // console.log('Current player state:', state);
        
        if (state.isCombinedAudio) {
          // console.log('Playing combined audio, auto-pause disabled');
          return;
        }
        
        if (state.hasPaused) {
          // console.log('Already paused once, playing through to end');
          setShowRecordingButton(false);
        } else if (state.shouldAutoPause) {
          // console.log('Setting up auto-pause at:', state.pausePoint);
          const onAudioProcess = (currentTime) => {
            if (currentTime >= state.pausePoint && !state.hasPaused) {
              // console.log('Reached pause point, pausing playback');
              wavesurfer.pause();
              setShowRecordingButton(true);
              state.hasPaused = true;
              setCurrentSilenceRegion(state.silenceRegions[0]);
              wavesurfer.un('audioprocess', onAudioProcess);
            }
          };
          wavesurfer.on('audioprocess', onAudioProcess);
        }
      });

      wavesurfer.on('pause', () => {
        // console.log('WaveSurfer pause event');
        setIsPlaying(false);
      });

      wavesurfer.on('ready', async () => {
        const ws = wavesurfer;
        const container = waveformRef.current;

        // Log all properties of the WaveSurfer instance
        // console.log('[WS READY] WaveSurfer instance keys:', Object.keys(ws));
        // console.log('[WS READY] ws.regions:', ws.regions);
        // console.log('[WS READY] ws.plugins:', ws.plugins);
        // console.log('[WS READY] ws.renderer:', ws.renderer);

        // Get the regions plugin
        let regionsPlugin = ws.plugins?.regions;
        if (!regionsPlugin) {
          console.warn('[WS READY] Regions plugin not found in plugins, attempting to reinitialize...');
          // Try to reinitialize the regions plugin
          regionsPlugin = RegionsPlugin.create({
            dragSelection: false,
            color: 'rgba(111, 45, 168, 0.18)',
            slop: 5
          });
          ws.registerPlugin(regionsPlugin);
          ws.plugins.regions = regionsPlugin;
        }
        // console.log('[WS READY] regionsPlugin:', regionsPlugin);

        // Ensure the container is ready
        if (!ws.renderer || !ws.renderer.container) {
          console.warn('[WS READY] Container not ready, waiting...');
          await new Promise(resolve => {
            waitForContainer(ws, resolve);
          });
        }

        // Log initial state
        // console.log('[WS READY] container:', container);
        if (container) {
          // console.log('[WS READY] Before clearing, phoneme-label count:', container.querySelectorAll('.phoneme-label').length);
          // console.log('[WS READY] Before clearing, <region> count:', container.querySelectorAll('region').length);
          const overlays = container.querySelector('.wavesurfer-region-overlays');
          // console.log('[WS READY] overlays child count:', overlays ? overlays.childNodes.length : 'no overlays');
        }

        // Clear existing regions and overlays
        if (regionsPlugin) {
          // console.log('[WS READY] Clearing existing regions...');
          // First clear the regions plugin
          regionsPlugin.clearRegions();
          
          // Then force remove all overlays and elements
          if (container) {
            forceRemoveAllRegionOverlays(container);
          }
          
          // Wait a frame to ensure DOM updates
          await new Promise(resolve => requestAnimationFrame(resolve));
          
          // Clear the regions array in the plugin
          regionsPlugin.regions = [];
        }

        // Manage regions based on combined audio state
        if (playerStateRef.current.isCombinedAudio) {
          if (stratagem?.audio_clips?.[0]) {
            const audioFile = stratagem.audio_clips[0].file;
            const pausePoint = combinedPausePointRef.current;
            const userRecordingDuration = combinedUserRecordingDurationRef.current;
            if (pausePoint != null && userRecordingDuration != null) {
              // console.log('[WS READY] Adding shifted phoneme regions for combined audio:', { pausePoint, userRecordingDuration });
              await manageRegions(regionsPlugin, waveformRef, stratagem, audioFile, pausePoint, userRecordingDuration, userRecordingAlignmentRef, playerStateRef, viewMode);
            }
          }
        } else {
          if (stratagem?.audio_clips?.[0]) {
            const audioFile = stratagem.audio_clips[0].file;
            // console.log('[WS READY] Adding original phoneme regions for initial audio.');
            await manageRegions(regionsPlugin, waveformRef, stratagem, audioFile, null, 0, userRecordingAlignmentRef, playerStateRef, viewMode);
          }
        }

        // Log final state
        if (container) {
          // console.log('[WS READY] After region management, phoneme-label count:', container.querySelectorAll('.phoneme-label').length);
          // console.log('[WS READY] After region management, <region> count:', container.querySelectorAll('region').length);
          const overlays = container.querySelector('.wavesurfer-region-overlays');
          // console.log('[WS READY] overlays child count after:', overlays ? overlays.childNodes.length : 'no overlays');
        }

        // Reset player loading state after everything is ready
        setPlayerLoading(false);
      });

      wavesurfer.on('error', (error) => {
        console.error('WaveSurfer error:', error);
        setPlayerLoading(false);
      });

      wavesurfer.on('finish', () => {
        // Combined-audio switch is handled by switchToCombinedAudio
        if (isAwaitingCombinedSwitchRef.current) {
          return;
        }
        const state = playerStateRef.current;
        if (!state.isCombinedAudio) {
          state.hasPaused = false;
          setShowRecordingButton(false);
          setCurrentSilenceRegion(null);
        }
        wavesurfer.seekTo(0);
      });

      wavesurfer.on('seek', () => {
        // console.log('Audio seek event');
        const state = playerStateRef.current;
        if (state.isCombinedAudio) {
          // console.log('Skipping seek handling for combined audio');
          return;
        }
        
        const currentTime = wavesurfer.getCurrentTime();
        if (currentTime < state.pausePoint) {
          // console.log('Seeked before pause point, resetting pause state');
          state.hasPaused = false;
          setShowRecordingButton(false);
        }
      });

      // Load audio
      let audioUrl;
      if (isCombinedAudioRef.current && combinedAudio) {
        audioUrl = combinedAudio;
        // console.log('Loading combined audio:', audioUrl);
        wavesurfer.load(audioUrl);
      } else {
        const wavUrl = `/audio/stratagems/${stratagem.id}/${stratagem.audio_clips[0].file}`;
        const webmUrl = convertWavToWebmUrl(wavUrl);
        // console.log('Loading audio from:', webmUrl);

        fetch(webmUrl)
          .then(response => response.arrayBuffer())
          .then(arrayBuffer => audioContext.decodeAudioData(arrayBuffer))
          .then(audioBuffer => {
            // console.log('Audio decoded successfully');
            originalAudioBufferRef.current = audioBuffer;
            return loadAudio(audioBuffer);
          })
          .then(() => {
            return audioBufferToWav(originalAudioBufferRef.current);
          })
          .then(wavBlob => {
            const wavUrl = URL.createObjectURL(wavBlob);
            wavesurfer.load(wavUrl);
          })
          .catch(error => {
            console.error('Error loading audio:', error);
            wavesurfer.load(wavUrl);
          });
      }

      return () => {
        console.log('Cleaning up WaveSurfer instance');
        if (waveformRef.current) {
          // Force remove all regions and overlays
          forceRemoveAllRegionOverlays(waveformRef.current);
          // Clear the container
          waveformRef.current.innerHTML = '';
        }
        if (spectrogramRef.current) {
          spectrogramRef.current.innerHTML = '';
        }
        if (wavesurferRef.current) {
          try {
            wavesurferRef.current.destroy();
          } catch (e) {
            console.warn('Error destroying WaveSurfer:', e);
          }
          wavesurferRef.current = null;
        }
      };
    } catch (error) {
      console.error('Error initializing WaveSurfer:', error);
      setError('Failed to initialize audio player: ' + error.message);
    }
  }, [stratagem?.id, waveformRef.current]);

  const handlePlayPause = () => {
    console.log('Play/Pause button clicked');
    if (wavesurferRef.current) {
      const currentTime = wavesurferRef.current.getCurrentTime();
      const silenceRegions = silenceRegionsRef.current;
      
      if (silenceRegions && silenceRegions.length > 0 && !isCombinedAudioRef.current) {
        const region = silenceRegions[0];
        const pauseTime = (region.start + region.end) / 2;
        
        // If we're past the pause point and haven't recorded yet, reset the pause state
        if (currentTime > pauseTime && !hasPausedRef.current) {
          console.log('Past pause point, resetting pause state');
          hasPausedRef.current = false;
          setShowRecordingButton(false);
        }
      }
      
      wavesurferRef.current.playPause();
    }
  };

  // Add this function to handle view mode changes
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    // Force refresh regions when view mode changes
    if (wavesurferRef.current?.plugins?.regions) {
      const regionsPlugin = wavesurferRef.current.plugins.regions;
      if (stratagem?.audio_clips?.[0]) {
        const audioFile = stratagem.audio_clips[0].file;
        if (playerStateRef.current.isCombinedAudio) {
          const pausePoint = combinedPausePointRef.current;
          const userRecordingDuration = combinedUserRecordingDurationRef.current;
          if (pausePoint != null && userRecordingDuration != null) {
            manageRegions(regionsPlugin, waveformRef, stratagem, audioFile, pausePoint, userRecordingDuration, userRecordingAlignmentRef, playerStateRef, mode);
          }
        } else {
          manageRegions(regionsPlugin, waveformRef, stratagem, audioFile, null, 0, userRecordingAlignmentRef, playerStateRef, mode);
        }
      }
    }
  };

  if (loading) return <div className="section"><p className="subtitle">Loading stratagem...</p></div>;
  if (error) return <div className="section"><p className="subtitle has-text-danger">Error: {error}</p></div>;
  if (!stratagem) return <div className="section"><p className="subtitle">Stratagem not found</p></div>;

  return (
    <div className="section">
      {stratagem?.category_id && (
        <button 
          className="button is-link mb-4" 
          onClick={() => navigate(`/category/${stratagem.category_id}`)}
        >
          Back to {category?.name || 'Category'}
        </button>
      )}
      <h2 className="title is-2 mb-4">{stratagem.name}</h2>
      {isAdmin() && (
        <button
          className="button is-warning is-light mb-4"
          onClick={() => navigate(`/admin/stratagem/${stratagem.id}`)}
        >
          Edit chunks
        </button>
      )}
      {stratagem.audio_clips && stratagem.audio_clips.length > 0 ? (
        <div className="box">
          <div className="field mb-4">
            <div className="control">
              <div className="buttons has-addons">
                <button 
                  className={`button ${viewMode === 'word' ? 'is-primary' : ''}`}
                  onClick={() => handleViewModeChange('word')}
                >
                  Word View
                </button>
                <button 
                  className={`button ${viewMode === 'syllable' ? 'is-primary' : ''}`}
                  onClick={() => handleViewModeChange('syllable')}
                >
                  Syllable View
                </button>
                <button 
                  className={`button ${viewMode === 'phoneme' ? 'is-primary' : ''}`}
                  onClick={() => handleViewModeChange('phoneme')}
                >
                  Phoneme View
                </button>
              </div>
            </div>
          </div>
          <div ref={waveformRef} style={{ minHeight: '100px', border: '1px solid #383351', marginBottom: '1rem', borderRadius: '4px' }}></div>
          <div ref={spectrogramRef} style={{ minHeight: '100px', border: '1px solid #383351', marginBottom: '1rem', borderRadius: '4px' }}></div>
          <div className="buttons">
            <button 
              className={`button is-primary ${playerLoading ? 'is-loading' : ''}`}
              onClick={handlePlayPause}
              disabled={playerLoading}
            >
              {isPlaying ? '⏸ Pause' : '▶ Play'}
            </button>
            {isCombinedMode ? (
              <button
                className="button is-warning"
                onClick={resetRecording}
                disabled={playerLoading}
              >
                🔄 Try Again
              </button>
            ) : (showRecordingButton || isProcessingRecording) ? (
              <button
                className={`button ${isProcessingRecording ? 'is-info is-loading' : isRecording ? 'is-danger' : 'is-info'}`}
                onMouseDown={isProcessingRecording ? undefined : startRecording}
                onMouseUp={isProcessingRecording ? undefined : stopRecording}
                onMouseLeave={isProcessingRecording ? undefined : stopRecording}
                disabled={playerLoading || isProcessingRecording}
              >
                {isProcessingRecording
                  ? 'Processing...'
                  : isRecording
                    ? '🔴 Recording...'
                    : '🎤 Hold to Record'}
              </button>
            ) : (
              <div className="button-placeholder"></div>
            )}
          </div>
          {userRecording && (
            <div className="mt-4">
              <h3 className="title is-4 recording-label">Your Recording:</h3>
              <div className="waveform-container">
                <audio src={userRecording} controls />
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="subtitle">No audio clips available for this stratagem.</p>
      )}
    </div>
  );
}

export default StratagemView; 